import React, { useState, useEffect, useRef } from 'react';
import {
    ChevronLeft, Paperclip, Send, X, FileText,
    Calendar, User, Clock, CheckCircle2, AlertCircle,
    MessageSquare, ExternalLink, Download
} from 'lucide-react';
import { getAvatarColorScheme, getInitials } from '../../../utils/helpers';
import API_ENDPOINTS from '../../../config/apiEndpoints';
import { getStatusDescriptor, parseCustomDate, formatDate } from '../../../pages/Task/Components/common/premiumUtils';
import Alert from '../../../common/Alerts/Alert';
import AppointmentTypeIcon from '../../../pages/PhysicalAppointment/Components/AppointmentTypeIcon';
import './UniversalDetailContent.css';

const UniversalDetailContent = ({ id, type, userId, onBack, onViewLead, onScorecardMouseEnter, onScorecardMouseLeave, detailData }) => {
    const [detail, setDetail] = useState(null);
    const [comments, setComments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const fileInputRef = useRef(null);
    const commentsEndRef = useRef(null);
    const [alertConfig, setAlertConfig] = useState({ show: false, type: 'error', title: 'Required', message: '' });

    // Helper to format names from strings like "kmukesh343(Mukesh Kumar)"
    const formatDisplayName = (name) => {
        if (!name || name === '-' || name === '--') return name;
        const match = name.toString().match(/\(([^)]+)\)/);
        return match ? match[1] : name;
    };

    // Helper to format collaborators from JSON or array
    const formatCollaborators = (collabs) => {
        if (!collabs || collabs === "--") return "--";

        // Handle TaskCollaborationUsers array
        if (Array.isArray(collabs) && collabs.length > 0 && collabs[0].PersonName) {
            return collabs.map(u => formatDisplayName(u.PersonName)).join(', ');
        }

        let collabArray = [];
        try {
            if (typeof collabs === 'string' && collabs.startsWith('[')) {
                collabArray = JSON.parse(collabs);
            } else if (Array.isArray(collabs)) {
                collabArray = collabs;
            } else {
                return formatDisplayName(collabs);
            }
        } catch (e) {
            return formatDisplayName(collabs);
        }

        if (!Array.isArray(collabArray)) return formatDisplayName(collabs);

        return collabArray.map(user => {
            const displayName = user.fuDisplayName || user.PersonName || user.name || "";
            return formatDisplayName(displayName);
        }).join(', ');
    };

    const TOKEN = API_ENDPOINTS.TOKE.UNIVERSAL_TOKEN || "-2295521862261168";
     const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');

    // API Endpoints as provided by the user
    const TASK_DETAIL_API = "https://wcfkit19.azurewebsites.net/UserCRMCampaign/Service/LeadAssignmentService.asmx/GetTaskDetailById";
    const PHY_APP_DETAIL_API = "https://wcfkit19.azurewebsites.net/UserCRMCampaign/Service/ToDoService.asmx/GetPhysicalAppointmentById";
   
    const COMMENTS_API = `${NEWV3_BASE_URL}${API_ENDPOINTS.COMMON.GET_COMMENTS}`;
    const SAVE_COMMENT_API = "https://serviceskit19.azurewebsites.net/UserCRM/SaveTaskCommentMOB";

    useEffect(() => {
        if (detailData) {
            setDetail(detailData);
            // Even if we have partial data, fetch full details for tasks and appointments to ensure all metadata is visible
            if (type === 'task' || type === 'physical-appointment' || type === 'appointment' || type === 'virtualapt' || type === 'lead') {
                fetchData();
            } else {
                setIsLoading(false);
                if (type !== 'followup') {
                    fetchComments();
                }
            }
        } else if (id) {
            fetchData();
        }
    }, [id, type, detailData]);



    const scrollToBottom = () => {
        commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const promises = [fetchDetail()];
            if (type !== 'followup') {
                promises.push(fetchComments());
            }
            await Promise.all(promises);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchDetail = async () => {
        try {
            // Strip client-side deduplication suffixes (e.g. "12345_0" -> "12345")
            const cleanId = id ? id.toString().split('_')[0] : id;
            if (type === 'lead') {
                const payload = {
                    UserId: userId || "34594",
                    ParentId: localStorage.getItem("parentId") || "",
                    Token: localStorage.getItem("token") || "",
                    Details: JSON.stringify({ FilterText: `Id = ${cleanId}`, StartNo: 0, EndNo: 1, UserId: userId || "34594", ParentId: localStorage.getItem("parentId") || "" })
                };
                const response = await fetch("https://serviceskit19.azurewebsites.net/UserCRM/GetLeadDetailListNew", {
                    method: 'POST',
                    headers: { "Content-Type": "application/json; charset=UTF-8" },
                    body: JSON.stringify(payload)
                });
                if (response.ok) {
                    const result = await response.json();
                    if (result.Details && result.Details.data && result.Details.data.length > 0) {
                        setDetail(result.Details.data[0]);
                    } else if (result.Details && !result.Details.data) {
                        setDetail(result.Details);
                    }
                }
                return;
            }

            const api = type === 'physical-appointment' ? PHY_APP_DETAIL_API : TASK_DETAIL_API;
            const body = type === 'physical-appointment'
                ? { UserId: userId || "34594", phyAppointmentId: cleanId }
                : { Id: parseInt(cleanId, 10), UserId: userId || "34594" };

            const response = await fetch(api, {
                method: 'POST',
                headers: { "Content-Type": "application/json; charset=UTF-8" },
                body: JSON.stringify(body)
            });
            if (response.ok) {
                const result = await response.json();
                if (result.d) {
                    let parsed = typeof result.d === 'string' ? JSON.parse(result.d) : result.d;
                    if (Array.isArray(parsed)) {
                        parsed = parsed[0];
                    }
                    setDetail(parsed);
                }
            }
        } catch (error) {
            console.error("Error fetching detail:", error);
        }
    };

    const fetchComments = async () => {
        try {
            // Strip client-side deduplication suffixes (e.g. "12345_0" -> "12345")
            const cleanId = id ? id.toString().split('_')[0] : id;
            const response = await fetch(COMMENTS_API, {
                method: 'POST',
                headers: { "Content-Type": "application/json; charset=UTF-8" },
                body: JSON.stringify({
                    Token: TOKEN,
                    Details: {
                        Id: parseInt(cleanId, 10),
                        Mode: type === 'appointment' ? "A" : (type === 'physical-appointment' ? "P" : "T")
                    }
                })
            });
            if (response.ok) {
                const result = await response.json();
                if (result.Details) {
                    setComments(Array.isArray(result.Details) ? result.Details : []);
                }
            }
        } catch (error) {
            console.error("Error fetching comments:", error);
        }
    };

    const handleSendComment = async () => {
        if (!newComment.trim()) {
            setAlertConfig({
                show: true,
                type: 'error',
                title: 'Text Required',
                message: 'Please enter a comment before saving.'
            });
            return;
        }

        setIsSubmitting(true);
        try {
            // Strip client-side deduplication suffixes (e.g. "12345_0" -> "12345")
            const cleanId = id ? id.toString().split('_')[0] : id;
            const payload = {
                Token: TOKEN,
                taskAttachment: {
                    Remarks: newComment,
                    CreatedBy: parseInt(userId || "34594", 10),
                    TaskId: parseInt(cleanId, 10),
                    ParentId: parseInt(userId || "34594", 10),
                    Mode: "C"
                }
            };

            const formData = new FormData();
            formData.append('details', JSON.stringify(payload));
            if (selectedFile) {
                formData.append('file', selectedFile);
            }

            const response = await fetch(SAVE_COMMENT_API, {
                method: 'POST',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: formData
            });

            if (response.ok) {
                setNewComment('');
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
                await fetchComments();
                setTimeout(scrollToBottom, 100);
                setAlertConfig({
                    show: true,
                    type: 'success',
                    title: 'Success',
                    message: 'Comment saved successfully.'
                });
            }
        } catch (error) {
            console.error("Error saving comment:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
        }
    };


    const getStatusBadge = () => {
        if (!detail) return null;

        const status = detail.taskStatus || detail.status || 'Open';
        const dueDate = detail.EndDate;
        const completedDate = detail.CompletedOn;
        const title = detail.Title || '';
        const startDate = detail.StartDate;
        const statusLabel = detail.statusLabel;
        const outcomeName = detail.OutcomeName || detail.Outcomes?.[0]?.Text;
        const createdDate = detail.CreatedOn;

        const descriptor = getStatusDescriptor(status, dueDate, completedDate, title, startDate, statusLabel, outcomeName, createdDate);
        if (!descriptor) return null;

        const lowDesc = descriptor.toLowerCase();
        let statusClass = 'upcoming';
        if (lowDesc.includes('completed')) statusClass = 'completed';
        else if (lowDesc.includes('overdue')) statusClass = 'overdue';
        else if (lowDesc.includes('today')) statusClass = 'due-today';

        return <div className={`universal-status-badge ${statusClass}`}>{descriptor}</div>;
    };

    if (isLoading && !detail) {
        return (
            <div className="universal-detail-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <div className="loader">Loading...</div>
            </div>
        );
    }

    const personName = detail?.LeadView?.PersonName || detail?.PersonName || detail?.RelatedTo || detail?.Related || detail?.Name || detail?.name || detail?.personName || detail?.geGeoEntityName || detail?.EntityName || "N/A";
    const avatarStyles = getAvatarColorScheme(personName);

    return (
        <div className="universal-detail-container">
            <div className="universal-detail-card">
                <div className="universal-card-body">
                    {/* Header: Title & Status */}
                    <div className="universal-detail-header">
                        {type !== 'appointment' && type !== 'lead' && (
                            <div className="universal-title-icon-wrapper">
                                {type === 'followup' ? (
                                    (() => {
                                        const statusClass = detailData?._statusClass || detail?._statusClass || 'scheduled';
                                        const colorMap = {
                                            'overdue': '#ef4444',
                                            'due-today': '#22c55e',
                                            'scheduled': '#f59e0b',
                                            'no-followup': '#3b82f6'
                                        };
                                        const color = colorMap[statusClass] || '#3b82f6';
                                        const bgMap = {
                                            'overdue': '#fef2f2',
                                            'due-today': '#f0fdf4',
                                            'scheduled': '#fffbeb',
                                            'no-followup': '#eff6ff'
                                        };
                                        const bg = bgMap[statusClass] || '#eff6ff';
                                        return (
                                            <div style={{ color: color, background: bg, padding: '8px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <svg style={{ width: '22px', height: '22px', fill: 'currentColor' }} viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M485.9 696.6c-6.4 0-12.5-3.6-15.6-9.5-29.8-57.6-66.5-62.7-66.9-62.7-9.7-1-16.7-9.5-15.8-19.2 1-9.7 9.5-16.7 19.2-15.8 1.5 0.1 15.9 1.8 34.6 13.2 13.5 8.2 25.8 19.1 36.8 32.7 4.2-14.2 10-30.7 17.9-47.4 28.9-61.5 71-95.4 121.7-98 9.7-0.5 18 7 18.4 16.7 0.5 9.7-7 18-16.7 18.4-37.6 2-68.5 28.1-91.8 77.9-18.5 39.4-24.7 78.6-24.7 78.9-1.2 7.5-7 13.4-14.5 14.7-0.7 0-1.7 0.1-2.6 0.1z m0 0"></path>
                                                    <path d="M758.2 415.2H265.8c-9.7 0-17.5-7.8-17.5-17.5s7.8-17.5 17.5-17.5h492.5c9.7 0 17.5 7.8 17.5 17.5 0 9.6-7.9 17.5-17.6 17.5z m0 0"></path>
                                                    <path d="M722.6 214.2h-20.4V169c0-9.7-7.8-17.5-17.5-17.5s-17.5 7.8-17.5 17.5v45.2H356.8V169c0-9.7-7.8-17.5-17.5-17.5s-17.5 7.8-17.5 17.5v45.2h-20.4c-77.3 0-140.2 62.9-140.2 140.3v353.9c0 77.3 62.9 140.2 140.2 140.2h421.2c77.3 0 140.2-62.9 140.2-140.2v-354c0.1-77.3-62.8-140.2-140.2-140.2z m105.2 494.1c0 58-47.1 105.2-105.2 105.2H301.4c-58 0-105.2-47.1-105.2-105.2V354.4c0-58 47.1-105.2 105.2-105.2h20.4v45.2c0 9.7 7.8 17.5 17.5 17.5s17.5-7.8 17.5-17.5v-45.2h310.3v45.2c0 9.7 7.8 17.5 17.5 17.5s17.5-7.8 17.5-17.5v-45.2h20.4c58 0 105.2 47.1 105.2 105.2v353.9z m0 0"></path>
                                                </svg>
                                            </div>
                                        );
                                    })()
                                ) : type === 'physical-appointment' ? (
                                    <AppointmentTypeIcon iconClass={detail?.IconClass || detail?.iconClass || 'appointment'} size={18} />
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" id="Capa_1" enableBackground="new 0 0 512 512" height="18px" viewBox="0 0 512 512" width="18px">
                                        <g>
                                            <g>
                                                <path d="m338.554 452h-164.999c-12.407 0-22.5 10.093-22.5 22.5v37.5h142.5v-15h-127.5v-22.5c0-4.136 3.365-7.5 7.5-7.5h164.999c4.136 0 7.501 3.364 7.501 7.5v22.5h-37.499v15h52.499v-37.5c0-12.407-10.093-22.5-22.501-22.5z" fill="#3b82f6"></path>
                                                <path d="m256.055 353.932c45.491 0 82.5-37.009 82.5-82.5v-188.932c0-18.929-6.641-37.454-18.699-52.164l-11.6 9.51c9.865 12.035 15.299 27.183 15.299 42.653h-15v15h15v15h-15v15h15v15h-15v15h15v15h-15v15h15v15h-60.055v15h60.055v53.933c0 37.22-30.28 67.5-67.5 67.5s-67.5-30.28-67.5-67.5v-53.933h59.945v-15h-59.945v-15h44.998v-15h-44.998v-15h44.998v-15h-44.998v-15h44.998v-15h-44.998v-15h44.998v-15h-44.998c0-37.219 30.28-67.5 67.5-67.5 15.471 0 30.62 5.433 42.654 15.299l9.51-11.601c-14.709-12.056-33.235-18.697-52.164-18.697-45.491 0-82.5 37.01-82.5 82.5v188.931c0 45.491 37.009 82.501 82.5 82.501z" fill="#7dd7ff"></path>
                                                <path d="m398.555 202.499h-45v15h30v15h-30v15h15v23.934c0 62.033-50.468 112.5-112.5 112.5s-112.5-50.467-112.5-112.5v-23.934h14.89v-15h-30v-15h30v-15h-45v45h15.11v23.934c0 59.973 41.625 110.399 97.5 123.926v41.641h15v-38.955c4.922.579 9.925.887 15 .887s10.079-.307 15-.887v38.955h15v-41.643c55.875-13.527 97.5-63.953 97.5-123.926v-23.934h15z" fill="#7dd7ff"></path>
                                                <path d="m226.055 278.933c0 16.542 13.458 30 30 30s30-13.458 30-30-13.458-30-30-30-30 13.458-30 30zm30-15c8.271 0 15 6.729 15 15s-6.729 15-15 15-15-6.729-15-15 6.729-15 15-15z" fill="#7dd7ff"></path>
                                                <path d="m248.552 37.501h15v15h-15z" fill="#7dd7ff"></path>
                                                <path d="m293.555 232.498h15v15h-15z" fill="#7dd7ff"></path>
                                                <path d="m278.555 142.5h15v15h-15z" fill="#7dd7ff"></path>
                                                <path d="m278.555 172.499h15v15h-15z" fill="#7dd7ff"></path>
                                                <path d="m278.555 112.5h15v15h-15z" fill="#7dd7ff"></path>
                                                <path d="m278.555 82.5h15v15h-15z" fill="#7dd7ff"></path>
                                            </g>
                                        </g>
                                    </svg>
                                )}
                            </div>
                        )}
                        <h1 className="universal-detail-title">
                            {(() => {
                                let t = (
                                    detailData?.taskTitle || detailData?.TaskTitle || detailData?.title || detailData?.Title || detailData?.PersonName ||
                                    detail?.taskTitle || detail?.TaskTitle || detail?.title || detail?.Title || detail?.PersonName ||
                                    detail?.FollowupStatus || 'No Title'
                                ).toString();

                                // For tasks, appointments, and physical-appointments: return the title as-is.
                                // Only strip suffixes for other types (e.g. follow-ups) where the regex is intentional.
                                if (type === 'task' || type === 'virtualapt' || type === 'appointment' || type === 'physical-appointment' || type === 'lead') {
                                    return t.trim();
                                }

                                return t.replace(/#\d+/, '')
                                    .replace(/[_-](1775|1900|20\d{2})\d*/g, '')
                                    .replace(/[_-]\d{10,}/g, '')
                                    .trim();
                            })()}
                        </h1>
                    </div>

                    {/* Related To Section */}
                    <div className="universal-related-section">
                        <div
                            className="universal-related-avatar"
                            style={{ backgroundColor: avatarStyles.bg, color: avatarStyles.text }}
                            onMouseEnter={(e) => onScorecardMouseEnter?.(e, detail?.LeadView?.LeadID || detail?.LeadID || detail?.geEntityId || detail?.EntityId || detail?.leadId, detail?.Title || detail?.FollowupStatus)}
                            onMouseLeave={onScorecardMouseLeave}
                        >
                            {getInitials(personName)}
                        </div>
                        <div className="universal-related-info">
                            <span className="universal-related-label">Related To:</span>
                            <span className="universal-related-name" onClick={() => onViewLead?.(detail?.LeadID || detail?.leadId)}>
                                {personName}
                            </span>
                        </div>
                    </div>

                    {/* Metadata Grid */}
                    <div className="universal-metadata-grid">
                        {(() => {
                            if (type === 'lead') return null;
                            const val = formatDate(detail?.EndDate || detail?.FollowupDate || detailData?.endDate, true);
                            if (val === '-') return null;
                            return (
                                <div className="universal-metadata-item">
                                    <span className="universal-metadata-label">Due Date:</span>
                                    <span className="universal-metadata-value">{val}</span>
                                </div>
                            );
                        })()}
                        {(() => {
                            const val = formatDate(detail?.CreatedOn || detail?.CreatedDate || detailData?.createdDate, true);
                            if (val === '-') return null;
                            return (
                                <div className="universal-metadata-item">
                                    <span className="universal-metadata-label">Created On:</span>
                                    <span className="universal-metadata-value">{val}</span>
                                </div>
                            );
                        })()}

                        <div className="universal-metadata-item">
                            <span className="universal-metadata-label">Owner:</span>
                            <span className="universal-metadata-value">
                                {formatDisplayName(detail?.assigneduser || detail?.OwnerName || detail?.CreatorName || detail?.cuDisplayName || detail?.auDisplayName || detail?.OwnerDisplayName || detail?.AgentName || detailData?.ownerName || '-')}
                            </span>
                        </div>
                        {type !== 'lead' && (() => {
                            const val = formatDate(detail?.CompletedOn || detail?.UpdatedOn || detailData?.completedDate, true);
                            if (val === '-') return null;
                            return (
                                <div className="universal-metadata-item">
                                    <span className="universal-metadata-label">Completed On:</span>
                                    <span className="universal-metadata-value">{val}</span>
                                </div>
                            );
                        })()}
                        <div className="universal-metadata-item">
                            <span className="universal-metadata-label">Outcomes:</span>
                            <span className="universal-metadata-value">{detail?.FollowupStatus || detail?.OutcomeName || detail?.Outcome || detail?.otOutcomeName || detail?.Outcomes?.[0]?.Text || detailData?.outcomeName || "--"}</span>
                        </div>
                        {type !== 'lead' && (
                            <div className="universal-metadata-item">
                                <span className="universal-metadata-label">Collaborators:</span>
                                <span className="universal-metadata-value">
                                    {formatCollaborators(detail?.TaskCollaborationUsers || detail?.CollaboratorList || detail?.Collaborators || "--")}
                                </span>
                            </div>
                        )}

                        {/* Dynamic Document Field */}
                        {(detail?.DocumentPath || detail?.FilePath || detail?.FileLocation || detail?.DocumentUrl) && (
                            <div className="universal-metadata-item" style={{ gridColumn: 'span 2' }}>
                                <span className="universal-metadata-label">Document:</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span className="universal-metadata-value" style={{ color: '#475569' }}>
                                        {detail.DocumentName || detail.FileName || 'Document attached'}
                                    </span>
                                    <button
                                        className="universal-attachment-link"
                                        onClick={() => window.open(detail.DocumentPath || detail.FilePath || detail.FileLocation || detail.DocumentUrl, '_blank')}
                                        style={{
                                            cursor: 'pointer',
                                            backgroundColor: '#eff6ff',
                                            border: 'none',
                                            padding: '4px',
                                            borderRadius: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#3b82f6'
                                        }}
                                    >
                                        <ExternalLink size={14} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Description Section */}
                    <div className="universal-description-section">
                        <span className="universal-description-label">Description:</span>
                        <p className="universal-description-text">
                            {detail?.Description || detail?.Remarks || "No description provided."}
                        </p>
                    </div>

                    {type !== 'followup' && type !== 'lead' && (
                        <>
                            <hr style={{ border: 'none', borderTop: '1px solid #f1f5f9', margin: '32px 0' }} />

                            {/* Comments Section */}
                            <div className="universal-comments-container">
                                <h3 className="universal-comments-title">
                                    <MessageSquare size={18} />
                                    Comments
                                </h3>

                                <div className="universal-comments-list">
                                    {comments.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', fontSize: '14px' }}>
                                            No comments yet.
                                        </div>
                                    ) : (
                                        comments.filter(c => c.display_name !== 'System').map((comment, idx) => (
                                            <div key={idx} className="universal-comment-item">
                                                <div className="universal-comment-avatar">
                                                    {(() => {
                                                        let avatarUrl = comment.Image || comment.image || "";
                                                        if (avatarUrl && !avatarUrl.startsWith('http')) {
                                                            avatarUrl = `https://kit19.com${avatarUrl.startsWith('/') ? '' : '/'}${avatarUrl}`;
                                                        }
                                                        const hasImage = avatarUrl && !avatarUrl.includes('no-profile') && !avatarUrl.includes('noimage');

                                                        return (
                                                            <>
                                                                {hasImage ? (
                                                                    <img
                                                                        src={avatarUrl}
                                                                        alt={comment.display_name}
                                                                        className="universal-comment-profile-img"
                                                                        onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                                                                    />
                                                                ) : null}
                                                                <div
                                                                    className="universal-related-avatar"
                                                                    style={{
                                                                        width: '32px', height: '32px', fontSize: '12px',
                                                                        backgroundColor: getAvatarColorScheme(comment.display_name || 'U').bg,
                                                                        color: getAvatarColorScheme(comment.display_name || 'U').text,
                                                                        display: hasImage ? 'none' : 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        borderRadius: '50%',
                                                                        fontWeight: 'bold'
                                                                    }}
                                                                >
                                                                    {getInitials(comment.display_name || 'U')}
                                                                </div>
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                                <div className="universal-comment-content">
                                                    <div className="universal-comment-header">
                                                        <span className="universal-comment-author">{comment.display_name}</span>
                                                        <span className="universal-comment-date">{comment.created_on}</span>
                                                    </div>
                                                    <p className="universal-comment-text">{comment.remarks || comment.Remarks}</p>

                                                    {comment.attachments && comment.attachments.length > 0 && (
                                                        <div className="universal-comment-attachments">
                                                            {comment.attachments.map((att, aIdx) => (
                                                                <a
                                                                    key={aIdx}
                                                                    href={att.FileUrl || att.FileLocation}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="universal-attachment-link"
                                                                >
                                                                    <Paperclip size={12} />
                                                                    {att.FileName || 'Attachment'}
                                                                </a>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                    <div ref={commentsEndRef} />
                                </div>

                                {/* Comment Input Section */}
                                <div className="universal-comment-input-section">
                                    <div className="universal-comment-input-wrapper">
                                        {selectedFile && (
                                            <div className="universal-file-preview">
                                                <Paperclip size={14} />
                                                <span>{selectedFile.name}</span>
                                                <div className="universal-remove-file" onClick={() => setSelectedFile(null)}>
                                                    <X size={14} style={{ marginLeft: '4px' }} />
                                                </div>
                                            </div>
                                        )}
                                        <div className="universal-input-controls">
                                            <input
                                                type="text"
                                                className="universal-comment-input"
                                                placeholder="Type your comment here..."
                                                value={newComment}
                                                onChange={(e) => setNewComment(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && handleSendComment()}
                                            />
                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                style={{ display: 'none' }}
                                                onChange={handleFileChange}
                                            />
                                            <button
                                                className="universal-icon-btn"
                                                onClick={() => fileInputRef.current?.click()}
                                                title="Attach file"
                                            >
                                                <Paperclip size={20} />
                                            </button>
                                            <button
                                                className="universal-icon-btn send-btn"
                                                onClick={handleSendComment}
                                                disabled={isSubmitting || (!newComment.trim() && !selectedFile)}
                                            >
                                                <Send size={20} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <Alert
                show={alertConfig.show}
                type={alertConfig.type}
                title={alertConfig.title}
                message={alertConfig.message}
                onConfirm={() => setAlertConfig({ ...alertConfig, show: false })}
            />
        </div>
    );
};

export default UniversalDetailContent;
